import cx from 'classnames';

import { Icon, Text, Tooltip } from '../../common/components';
import { t } from '../../common/i18n';
import { formatDate } from '../../common/utils/Date/Date';
import { resolveDeadlineIcon } from '../config/deadlineCategories';
import { Deadline } from '../types';
import { getStatusKind, relativeLabel, statusColor } from '../utils/deadlineStatus';
import CheckCircleIcon from './CheckCircleIcon';
import PriorityIcon from './PriorityIcon';

const namespace = 'Deadlines';

interface DeadlineCardProps {
  deadline: Deadline;
  onComplete: (deadline: Deadline, completed: boolean) => void;
  onEdit: (deadline: Deadline) => void;
  onDelete: (deadline: Deadline) => void;
}

export const DeadlineCard: React.FC<DeadlineCardProps> = ({ deadline, onComplete, onEdit, onDelete }) => {
  const kind = getStatusKind(deadline);
  const isCompleted = kind === 'completed';

  return (
    <div className={cx('DeadlineCard', `DeadlineCard--${kind}`)}>
      <div className="DeadlineCard__icon">
        {isCompleted ? (
          <CheckCircleIcon className="DeadlineCard__check" />
        ) : (
          <Icon name={resolveDeadlineIcon(deadline.icon, deadline.category)} />
        )}
      </div>

      <div className="DeadlineCard__body">
        <Text as="div" className="DeadlineCard__title" size="body-medium" weight="600" color="Neutral800">
          {deadline.title}
          {deadline.note && (
            <Tooltip text={deadline.note} wrapper={false}>
              <span className="DeadlineCard__noteIcon" tabIndex={0} aria-label={deadline.note}>
                <Icon name="info" />
              </span>
            </Tooltip>
          )}
        </Text>
        <Text as="div" className="DeadlineCard__meta" size="body-small" color="Neutral600">
          {formatDate(deadline.dueDate)} <span className="DeadlineCard__dot">·</span>{' '}
          <Text as="span" size="body-small" weight="600" color={statusColor(kind)}>
            {relativeLabel(deadline)}
          </Text>
          {deadline.recurring && (
            <>
              {' '}
              <span className="DeadlineCard__dot">·</span> {t('relative.recurring', namespace)}
            </>
          )}
        </Text>
      </div>

      {deadline.priority !== 'normal' && (
        <span
          className={cx('DeadlineCard__priority', `DeadlineCard__priority--${deadline.priority}`)}
          title={`${t(`priority.${deadline.priority}`, namespace)} priorita`}
          aria-label={`${t(`priority.${deadline.priority}`, namespace)} priorita`}
        >
          <PriorityIcon priority={deadline.priority} />
        </span>
      )}

      <div className="DeadlineCard__actions">
        {deadline.completable && !isCompleted && (
          <button
            type="button"
            className="DeadlineCard__action"
            onClick={() => onComplete(deadline, true)}
            aria-label={t('actions.complete', namespace)}
          >
            <Icon name="check" className="DeadlineCard__action-icon" />
            <span>{t('actions.complete', namespace)}</span>
          </button>
        )}

        {deadline.completable && isCompleted && (
          <button
            type="button"
            className="DeadlineCard__action"
            onClick={() => onComplete(deadline, false)}
            aria-label={t('actions.undo', namespace)}
          >
            <span>{t('actions.undo', namespace)}</span>
          </button>
        )}

        {deadline.editable && (
          <>
            <button
              type="button"
              className="DeadlineCard__action DeadlineCard__action--icon"
              onClick={() => onEdit(deadline)}
              aria-label={t('actions.edit', namespace)}
            >
              <Icon name="edit" className="DeadlineCard__action-icon" />
            </button>
            <button
              type="button"
              className="DeadlineCard__action DeadlineCard__action--icon"
              onClick={() => onDelete(deadline)}
              aria-label={t('actions.delete', namespace)}
            >
              <Icon name="trash" className="DeadlineCard__action-icon" />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default DeadlineCard;
